import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DynamicDrawUsage,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  MeshLambertMaterial,
  Scene,
  type CanvasTexture,
} from 'three';
import { hash2, hash3, hashToUnit, valueNoise2 } from '../core/math';
import {
  BlockType,
  type GameConfig,
  type LevelBounds,
  type Vector3D,
} from '../types';
import { EnemyKind, bossKindForStage, isBossStage } from '../entities/enemyCatalog';
import {
  BLOCK_ATLAS_COLS,
  BLOCK_ATLAS_INDEX,
  BLOCK_ATLAS_ROWS,
  createBlockAtlasTexture,
} from './blockTextures';
import { ExitPortalDecor } from './ExitPortalDecor';

/** Soft multiply tint on top of atlas (keeps ores vivid, dirt/stone grounded). */
const BLOCK_TINTS: Readonly<Record<BlockType, Color>> = {
  [BlockType.Air]: new Color(0xffffff),
  [BlockType.Dirt]: new Color(0xffffff),
  [BlockType.Stone]: new Color(0xffffff),
  [BlockType.Iron]: new Color(0xe8f0ff),
  [BlockType.Gold]: new Color(0xfff0c0),
  [BlockType.SurfacePortal]: new Color(0xffffff),
  [BlockType.ExitDoor]: new Color(0xffc080),
  [BlockType.Bedrock]: new Color(0xffffff),
  [BlockType.Shop]: new Color(0xffe0a0),
};

const HIDDEN_MATRIX = new Matrix4().makeScale(0, 0, 0);

/**
 * Vertical stack of sealed Spelunky-style levels under a surface base camp.
 *
 * Layout (top → bottom):
 *   [Base Camp]  — solid floor, SurfacePortal door (no open shaft)
 *   [Level 1]    — sealed chamber, entrance + ExitDoor
 *   [Level 2..N] — same pattern, each floor/ceiling is Bedrock
 *
 * Indexing: `index = x + z * width + y * width * depth`
 */
export class WorldManager {
  readonly width: number;
  readonly height: number;
  readonly depth: number;
  readonly blockSize: number;
  readonly surfaceCampLayers: number;
  readonly levelHeight: number;
  readonly levelCount: number;
  readonly maxBlocks: number;

  /** Dense voxel storage — one byte per cell. */
  readonly blocks: Uint8Array;

  private readonly indexToInstance: Int32Array;
  private readonly instanceToIndex: Int32Array;
  private readonly levels: LevelBounds[] = [];
  private campPortal: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 };
  private campSpawn: Vector3D = { x: 0, y: 0, z: 0 };

  private instanceCount = 0;
  private readonly mesh: InstancedMesh;
  private readonly matrix = new Matrix4();
  private readonly scratchColor = new Color();
  private readonly atlasIndexAttr: InstancedBufferAttribute;
  private readonly atlasTexture: CanvasTexture;
  private readonly exitDecor: ExitPortalDecor;
  private seed: number;
  private runId = 0;
  /** Endless stage number of the first physical slot in this column. */
  private columnStartStage = 1;
  /** Digs relative to the last seed-generated layout (index → block type). */
  private readonly digs = new Map<number, number>();

  /**
   * Only voxels in this inclusive Y band get InstancedMesh entries.
   * Players share one stage, so meshing the whole column is wasted fill-rate.
   */
  private visibleMinY = 0;
  private visibleMaxY = 0;
  /** When false, setBlockRaw only writes voxel data (used during generate). */
  private meshingEnabled = true;

  constructor(scene: Scene, config: GameConfig) {
    this.width = config.worldWidth;
    this.height = config.worldHeight;
    this.depth = config.worldDepth;
    this.blockSize = config.blockSize;
    this.surfaceCampLayers = config.surfaceCampLayers;
    this.levelHeight = config.levelHeight;
    this.levelCount = config.levelCount;
    this.seed = config.worldSeed >>> 0;
    this.maxBlocks = this.width * this.height * this.depth;

    const requiredHeight =
      this.surfaceCampLayers + this.levelCount * this.levelHeight;
    if (this.height < requiredHeight) {
      throw new Error(
        `worldHeight ${this.height} < required ${requiredHeight} ` +
          `(camp ${this.surfaceCampLayers} + ${this.levelCount}×${this.levelHeight})`,
      );
    }

    this.blocks = new Uint8Array(this.maxBlocks);
    this.indexToInstance = new Int32Array(this.maxBlocks).fill(-1);
    this.instanceToIndex = new Int32Array(this.maxBlocks).fill(-1);

    this.atlasTexture = createBlockAtlasTexture();
    const geometry = createUnitBoxGeometry();
    this.atlasIndexAttr = new InstancedBufferAttribute(new Float32Array(this.maxBlocks), 1);
    this.atlasIndexAttr.setUsage(DynamicDrawUsage);
    geometry.setAttribute('instanceAtlasIndex', this.atlasIndexAttr);

    const material = createAtlasLambertMaterial(this.atlasTexture);
    this.mesh = new InstancedMesh(geometry, material, this.maxBlocks);
    this.mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.count = 0;
    // Whole-column AABB would almost never cull; we cull by visible Y band instead.
    this.mesh.frustumCulled = false;
    this.visibleMinY = 0;
    this.visibleMaxY = this.height - 1;

    const colors = new Float32Array(this.maxBlocks * 3);
    this.mesh.instanceColor = new InstancedBufferAttribute(colors, 3);
    this.mesh.instanceColor.setUsage(DynamicDrawUsage);

    scene.add(this.mesh);
    this.exitDecor = new ExitPortalDecor(scene, this, this.blockSize);
  }

  getMesh(): InstancedMesh {
    return this.mesh;
  }

  getActiveInstanceCount(): number {
    return this.instanceCount;
  }

  getLevelCount(): number {
    return this.levelCount;
  }

  /** 1-based level bounds, or null if out of range. */
  getLevel(level: number): LevelBounds | null {
    if (level < 1 || level > this.levels.length) return null;
    return this.levels[level - 1]!;
  }

  getCampPortalVoxel(): { x: number; y: number; z: number } {
    return { ...this.campPortal };
  }

  indexOf(x: number, y: number, z: number): number {
    if (x < 0 || y < 0 || z < 0 || x >= this.width || y >= this.height || z >= this.depth) {
      return -1;
    }
    return x + z * this.width + y * this.width * this.depth;
  }

  inBounds(x: number, y: number, z: number): boolean {
    return x >= 0 && y >= 0 && z >= 0 && x < this.width && y < this.height && z < this.depth;
  }

  getBlock(x: number, y: number, z: number): BlockType {
    const i = this.indexOf(x, y, z);
    return i < 0 ? BlockType.Bedrock : (this.blocks[i] as BlockType);
  }

  isSolid(x: number, y: number, z: number): boolean {
    const t = this.getBlock(x, y, z);
    return (
      t !== BlockType.Air &&
      t !== BlockType.SurfacePortal &&
      t !== BlockType.ExitDoor &&
      t !== BlockType.Shop
    );
  }

  voxelCenter(x: number, y: number, z: number, out: Vector3D): Vector3D {
    const s = this.blockSize;
    out.x = (x + 0.5) * s;
    out.y = (y + 0.5) * s;
    out.z = (z + 0.5) * s;
    return out;
  }

  worldToVoxel(wx: number, wy: number, wz: number): { x: number; y: number; z: number } {
    const s = this.blockSize;
    return {
      x: Math.floor(wx / s),
      y: Math.floor(wy / s),
      z: Math.floor(wz / s),
    };
  }

  /**
   * Which dive level contains this world Y (1..levelCount), or 0 for camp / void.
   */
  levelAtWorldY(worldY: number): number {
    const voxelY = Math.floor(worldY / this.blockSize);
    for (let i = 0; i < this.levels.length; i++) {
      const lvl = this.levels[i]!;
      if (voxelY >= lvl.floorY && voxelY <= lvl.ceilingY) {
        return i + 1;
      }
    }
    return 0;
  }

  getSeed(): number {
    return this.seed;
  }

  getRunId(): number {
    return this.runId;
  }

  setRunId(runId: number): void {
    this.runId = Math.max(0, runId | 0);
  }

  /** Sparse dig list for multiplayer persistence. */
  exportDigs(): Array<{ i: number; t: number }> {
    const out: Array<{ i: number; t: number }> = [];
    for (const [i, t] of this.digs) {
      out.push({ i, t });
    }
    return out;
  }

  /** Apply digs on top of the current generated world (after regenerate/generate). */
  applyDigs(digs: ReadonlyArray<{ i: number; t: number }>): void {
    for (const dig of digs) {
      if (dig.i < 0 || dig.i >= this.maxBlocks) continue;
      const x = dig.i % this.width;
      const z = Math.floor(dig.i / this.width) % this.depth;
      const y = Math.floor(dig.i / (this.width * this.depth));
      this.modifyBlock(x, y, z, dig.t as BlockType);
    }
  }

  getColumnStartStage(): number {
    return this.columnStartStage;
  }

  /**
   * Rebuild the entire column with a new seed.
   * `columnStartStage` is the endless stage mapped to physical slot 1.
   */
  regenerate(seed?: number, columnStartStage = 1): number {
    this.seed =
      seed !== undefined
        ? seed >>> 0
        : ((Math.imul(this.seed ^ performance.now(), 0x85ebca6b) ^
            (Math.random() * 0xffffffff)) >>>
          0) || 1;
    this.runId += 1;
    this.generate(columnStartStage);
    return this.seed;
  }

  generate(columnStartStage = 1): void {
    this.columnStartStage = Math.max(1, columnStartStage | 0);
    this.digs.clear();
    this.clearInstances();
    this.blocks.fill(BlockType.Air);
    this.levels.length = 0;

    // Defer meshing until the column is fully carved — neighbors must be final
    // before we decide which voxels are exposed.
    this.meshingEnabled = false;

    const campFloorY = this.height - this.surfaceCampLayers;
    this.buildBaseCamp(campFloorY);

    for (let level = 1; level <= this.levelCount; level++) {
      const ceilingY = campFloorY - 1 - (level - 1) * this.levelHeight;
      const floorY = ceilingY - this.levelHeight + 1;
      if (floorY < 0) {
        throw new Error(`Level ${level} floorY ${floorY} is below world`);
      }
      const stageForSlot = this.columnStartStage + level - 1;
      if (level === this.levelCount && isBossStage(stageForSlot)) {
        this.buildBossArena(bossKindForStage(stageForSlot), floorY, ceilingY, level);
      } else {
        this.buildLevel(level, floorY, ceilingY);
      }
    }

    this.meshingEnabled = true;
    // Leave the mesh empty until Engine sets the active stage Y band.
    this.visibleMinY = 0;
    this.visibleMaxY = -1;
    this.exitDecor.clear();
  }

  /**
   * Restrict GPU instances + exit portals to an inclusive voxel Y band.
   * Call on stage enter so only the active sealed level is drawn.
   */
  setVisibleYRange(minY: number, maxY: number, force = false): void {
    const nextMin = Math.max(0, Math.min(this.height - 1, Math.floor(minY)));
    const nextMax = Math.max(nextMin, Math.min(this.height - 1, Math.floor(maxY)));
    if (!force && nextMin === this.visibleMinY && nextMax === this.visibleMaxY) {
      return;
    }
    this.visibleMinY = nextMin;
    this.visibleMaxY = nextMax;
    this.rebuildVisibleInstances();
    this.exitDecor.rebuild(this.visibleMinY, this.visibleMaxY);
  }

  /** Toggle receiveShadow on the world mesh (cheap on desktop, skip on weak GPUs). */
  setReceiveShadow(enabled: boolean): void {
    this.mesh.receiveShadow = enabled;
  }

  /** Enable/disable PointLights on exit portal props. */
  setPortalLightsEnabled(enabled: boolean): void {
    this.exitDecor.setLightsEnabled(enabled);
  }

  modifyBlock(x: number, y: number, z: number, newType: BlockType): BlockType | null {
    const i = this.indexOf(x, y, z);
    if (i < 0) return null;

    const previous = this.blocks[i] as BlockType;
    if (previous === newType) return previous;
    // Bedrock / doors are never overwritten by mining callers, but guard anyway.
    if (previous === BlockType.Bedrock) return previous;

    this.blocks[i] = newType;
    this.digs.set(i, newType);

    // Digging can expose buried neighbors — refresh the cell + 6-adjacent.
    this.refreshInstanceAt(x, y, z);
    this.refreshInstanceAt(x + 1, y, z);
    this.refreshInstanceAt(x - 1, y, z);
    this.refreshInstanceAt(x, y + 1, z);
    this.refreshInstanceAt(x, y - 1, z);
    this.refreshInstanceAt(x, y, z + 1);
    this.refreshInstanceAt(x, y, z - 1);

    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) {
      this.mesh.instanceColor.needsUpdate = true;
    }
    return previous;
  }

  getBaseCampSpawn(): Vector3D {
    return { ...this.campSpawn };
  }

  getLevelSpawn(level: number): Vector3D | null {
    const bounds = this.getLevel(level);
    return bounds ? { ...bounds.entrance } : null;
  }

  depthBelowSurface(worldY: number): number {
    const campFloorY = this.height - this.surfaceCampLayers;
    const voxelY = Math.floor(worldY / this.blockSize);
    return Math.max(0, campFloorY - voxelY);
  }

  isInBaseCamp(worldY: number): boolean {
    const voxelY = Math.floor(worldY / this.blockSize);
    return voxelY >= this.height - this.surfaceCampLayers;
  }

  dispose(): void {
    this.exitDecor.dispose();
    this.mesh.geometry.dispose();
    (this.mesh.material as MeshLambertMaterial).dispose();
    this.atlasTexture.dispose();
    this.mesh.removeFromParent();
  }

  /** Pulse exit portal glow (call from render / fixed update). */
  updateDecor(dt: number): void {
    this.exitDecor.update(dt);
  }

  /** Dim / redden exit portals while a boss fight is active. */
  setExitLocked(locked: boolean): void {
    this.exitDecor.setLocked(locked);
  }

  // ---------------------------------------------------------------------------
  // Generation — base camp
  // ---------------------------------------------------------------------------

  private buildBaseCamp(campFloorY: number): void {
    const portalX = (this.width / 2) | 0;
    const portalZ = (this.depth / 2) | 0;
    this.campPortal = { x: portalX, y: campFloorY + 1, z: portalZ };

    for (let y = campFloorY; y < this.height; y++) {
      for (let z = 0; z < this.depth; z++) {
        for (let x = 0; x < this.width; x++) {
          let type = BlockType.Air;

          if (y === campFloorY) {
            // Solid sealed floor — no open shaft. Portal sits ON the floor as a door.
            type = BlockType.Stone;
          } else {
            const onRim =
              x === 0 || z === 0 || x === this.width - 1 || z === this.depth - 1;
            if (onRim && y < this.height - 1) {
              type = BlockType.Bedrock;
            }
          }

          if (type === BlockType.Air) continue;
          this.setBlockRaw(x, y, z, type);
        }
      }
    }

    // 2-tall sealed roof marker (not a return portal — progression is forward-only).
    // Leave as bedrock rim only; no SurfacePortal.

    // Clear a small pad around the portal so the player isn't inside a wall.
    for (let dz = -2; dz <= 2; dz++) {
      for (let dx = -2; dx <= 2; dx++) {
        if (dx === 0 && dz === 0) continue;
        const x = portalX + dx;
        const z = portalZ + dz;
        if (!this.inBounds(x, campFloorY + 1, z)) continue;
        this.setBlockRaw(x, campFloorY + 1, z, BlockType.Air);
        this.setBlockRaw(x, campFloorY + 2, z, BlockType.Air);
      }
    }

    // Player position is AABB center; sit feet on top of the camp floor voxel.
    this.campSpawn = {
      x: (portalX + 3 + 0.5) * this.blockSize,
      y: (campFloorY + 1) * this.blockSize + 0.9,
      z: (portalZ + 0.5) * this.blockSize,
    };
    // Keep spawn inside bounds.
    this.campSpawn.x = Math.min(
      (this.width - 2) * this.blockSize,
      Math.max(2 * this.blockSize, this.campSpawn.x),
    );
  }

  // ---------------------------------------------------------------------------
  // Generation — sealed dive levels
  // ---------------------------------------------------------------------------

  private buildLevel(level: number, floorY: number, ceilingY: number): void {
    // Mix world seed, run id, and level so each dive + stage is unique.
    const levelSeed =
      this.seed ^
      Math.imul(level, 0x9e3779b9) ^
      Math.imul(this.runId + 1, 0x85ebca6b);

    for (let y = floorY; y <= ceilingY; y++) {
      for (let z = 0; z < this.depth; z++) {
        for (let x = 0; x < this.width; x++) {
          const type = this.sampleFill(x, y, z, floorY, ceilingY, level, levelSeed);
          this.setBlockRaw(x, y, z, type);
        }
      }
    }

    // Randomize entrance/exit among four wall sides (never the same side).
    const entranceSide = hash2(level, 3, levelSeed) % 4;
    let exitSide = hash2(level, 9, levelSeed ^ 0xabcdef) % 4;
    if (exitSide === entranceSide) exitSide = (exitSide + 2) % 4;

    const entrance = this.pickLandmark(levelSeed ^ 0x1111, entranceSide, floorY);
    const exit = this.pickLandmark(levelSeed ^ 0x2222, exitSide, floorY);

    // Shop on a remaining wall side — must be found via the maze.
    let shopSide = hash2(level, 23, levelSeed ^ 0x5a0f) % 4;
    if (shopSide === entranceSide || shopSide === exitSide) {
      shopSide = [0, 1, 2, 3].find((s) => s !== entranceSide && s !== exitSide) ?? 0;
    }
    const shopLandmark = this.pickLandmark(levelSeed ^ 0x3333, shopSide, floorY);
    const shopJitter = hash2(level, 29, levelSeed);
    let shopX = Math.max(3, Math.min(this.width - 4, shopLandmark.x + ((shopJitter % 5) - 2)));
    let shopZ = Math.max(3, Math.min(this.depth - 4, shopLandmark.z + (((shopJitter >>> 8) % 5) - 2)));
    if (
      (shopX === entrance.x && shopZ === entrance.z) ||
      (shopX === exit.x && shopZ === exit.z)
    ) {
      shopX = Math.max(3, Math.min(this.width - 4, ((entrance.x + exit.x) / 2) | 0));
      shopZ = Math.max(3, Math.min(this.depth - 4, ((entrance.z + exit.z) / 2) | 0));
    }

    type MazeRoom = { x: number; y: number; z: number };
    const rooms: MazeRoom[] = [
      { x: entrance.x, y: floorY + 1, z: entrance.z },
      { x: exit.x, y: floorY + 1, z: exit.z },
      { x: shopX, y: floorY + 1, z: shopZ },
    ];

    const roomScale = 2 + (hash2(level, 5, levelSeed) % 3);
    this.carveRoom(entrance.x, floorY + 1, entrance.z, roomScale, 3, roomScale);
    this.carveRoom(exit.x, floorY + 1, exit.z, roomScale, 3, roomScale);
    this.carveRoom(shopX, floorY + 1, shopZ, 2, 2, 2);

    // Interior junctions — the maze graph nodes between landmarks.
    const junctionCount = 4 + (hash2(level, 11, levelSeed) % 4); // 4–7
    const minSep = 8;
    for (let i = 0; i < junctionCount; i++) {
      let placed: MazeRoom | null = null;
      for (let attempt = 0; attempt < 24; attempt++) {
        const h = hash3(i, attempt, level, levelSeed ^ 0x7a11);
        const rx = 4 + (h % Math.max(1, this.width - 8));
        const rz = 4 + ((h >>> 8) % Math.max(1, this.depth - 8));
        const ry =
          floorY +
          1 +
          ((h >>> 16) % Math.max(1, Math.min(3, ceilingY - floorY - 4)));
        const tooClose = rooms.some(
          (r) => Math.hypot(r.x - rx, r.z - rz) < minSep,
        );
        if (tooClose) continue;
        placed = { x: rx, y: ry, z: rz };
        break;
      }
      if (!placed) continue;
      rooms.push(placed);
      const h = hash3(i, level, 19, levelSeed);
      const rr = 1 + ((h >>> 8) % 3);
      this.carveRoom(placed.x, placed.y, placed.z, rr, 1 + (h % 3), rr);
    }

    // Spanning tree: connect each new room to its nearest already-linked room.
    // Guarantees reachability without a single entrance→exit hallway.
    const linked = new Set<number>([0]);
    const edges: Array<[number, number]> = [];
    while (linked.size < rooms.length) {
      let bestI = -1;
      let bestJ = -1;
      let bestDist = Infinity;
      for (const i of linked) {
        for (let j = 0; j < rooms.length; j++) {
          if (linked.has(j)) continue;
          const d =
            Math.hypot(rooms[i].x - rooms[j].x, rooms[i].z - rooms[j].z) +
            hashToUnit(hash2(i, j, levelSeed ^ 0x51a0)) * 4;
          if (d < bestDist) {
            bestDist = d;
            bestI = i;
            bestJ = j;
          }
        }
      }
      if (bestI < 0 || bestJ < 0) break;
      edges.push([bestI, bestJ]);
      linked.add(bestJ);
    }

    // Extra loop corridors so there are alternate routes (true maze, not a tree).
    const loopCount = 2 + (hash2(level, 41, levelSeed) % 3); // 2–4
    let loopsAdded = 0;
    let loopGuard = 0;
    while (loopsAdded < loopCount && loopGuard < 40) {
      loopGuard++;
      const a = hash2(loopGuard, level, levelSeed ^ 0x1000) % rooms.length;
      const b = hash2(loopGuard, level, levelSeed ^ 0x2000) % rooms.length;
      if (a === b) continue;
      const already = edges.some(
        ([u, v]) => (u === a && v === b) || (u === b && v === a),
      );
      if (already) continue;
      // Prefer medium-distance links — short ones collapse, long ones dominate.
      const dist = Math.hypot(rooms[a].x - rooms[b].x, rooms[a].z - rooms[b].z);
      if (dist < minSep || dist > 28) continue;
      edges.push([a, b]);
      loopsAdded++;
    }

    for (let e = 0; e < edges.length; e++) {
      const [a, b] = edges[e];
      this.carveWindingTunnel(
        rooms[a],
        rooms[b],
        floorY,
        ceilingY,
        levelSeed ^ Math.imul(e + 1, 0x9e3779b9),
      );
    }

    // A few dead-end pockets off random junctions (optional loot / dig sites).
    const pocketCount = 1 + (hash2(level, 17, levelSeed) % 3);
    for (let i = 0; i < pocketCount; i++) {
      const hub = rooms[1 + (hash2(i, level, levelSeed ^ 0xdead) % Math.max(1, rooms.length - 1))];
      const h = hash3(i, level, 7, levelSeed ^ 0xc0ff);
      const angle = hashToUnit(h) * Math.PI * 2;
      const dist = 5 + (h % 5);
      const px = Math.max(3, Math.min(this.width - 4, Math.round(hub.x + Math.cos(angle) * dist)));
      const pz = Math.max(3, Math.min(this.depth - 4, Math.round(hub.z + Math.sin(angle) * dist)));
      const py = floorY + 1 + ((h >>> 16) % Math.max(1, Math.min(2, ceilingY - floorY - 4)));
      const rr = 1 + ((h >>> 24) % 2);
      this.carveRoom(px, py, pz, rr, 1 + (h % 2), rr);
      this.carveWindingTunnel(
        hub,
        { x: px, y: py, z: pz },
        floorY,
        ceilingY,
        levelSeed ^ 0xb00b ^ i,
      );
    }

    this.setBlockRaw(exit.x, floorY + 1, exit.z, BlockType.ExitDoor);
    this.setBlockRaw(exit.x, floorY + 2, exit.z, BlockType.ExitDoor);

    this.setBlockRaw(entrance.x, floorY + 1, entrance.z, BlockType.Air);
    this.setBlockRaw(entrance.x, floorY + 2, entrance.z, BlockType.Air);

    this.setBlockRaw(shopX, floorY + 1, shopZ, BlockType.Shop);
    this.setBlockRaw(shopX, floorY + 2, shopZ, BlockType.Shop);

    const entranceWorld: Vector3D = {
      x: (entrance.x + 0.5) * this.blockSize,
      y: (floorY + 1) * this.blockSize + 0.9,
      z: (entrance.z + 0.5) * this.blockSize,
    };

    this.levels.push({
      floorY,
      ceilingY,
      entrance: entranceWorld,
      exitDoor: { x: exit.x, y: floorY + 1, z: exit.z },
      shop: { x: shopX, y: floorY + 1, z: shopZ },
      bossKind: null,
    });
  }

  /**
   * Custom sealed arena for boss stages (physical slot N).
   * No shop — entrance and exit on opposite walls.
   */
  private buildBossArena(
    bossKind: EnemyKind,
    floorY: number,
    ceilingY: number,
    level: number,
  ): void {
    const levelSeed =
      this.seed ^
      Math.imul(level, 0x9e3779b9) ^
      Math.imul(this.runId + 1, 0x85ebca6b) ^
      0xb055;

    for (let y = floorY; y <= ceilingY; y++) {
      for (let z = 0; z < this.depth; z++) {
        for (let x = 0; x < this.width; x++) {
          const type = this.sampleFill(x, y, z, floorY, ceilingY, level, levelSeed);
          if (type === BlockType.Air) continue;
          this.setBlockRaw(x, y, z, type);
        }
      }
    }

    const cx = (this.width / 2) | 0;
    const cz = (this.depth / 2) | 0;
    const walkY = floorY + 1;
    const roomH = Math.min(6, ceilingY - floorY - 2);

    let entrance: { x: number; y: number; z: number };
    let exit: { x: number; y: number; z: number };
    const pedestals: Array<{ x: number; y: number; z: number }> = [];

    if (bossKind === EnemyKind.TunnelHorror) {
      // Long rectangle along Z for charge collisions into end walls.
      const halfW = 6;
      const halfL = 14;
      this.carveRoom(cx, walkY, cz, halfW, roomH, halfL);
      entrance = { x: cx, y: walkY, z: Math.max(2, cz - halfL) };
      exit = { x: cx, y: walkY, z: Math.min(this.depth - 3, cz + halfL) };
      this.carveRoom(entrance.x, walkY, entrance.z, 2, 3, 2);
      this.carveRoom(exit.x, walkY, exit.z, 2, 3, 2);
    } else if (bossKind === EnemyKind.SpireMother) {
      this.carveRoom(cx, walkY, cz, 10, roomH, 10);
      entrance = { x: cx, y: walkY, z: Math.max(2, cz - 10) };
      exit = { x: cx, y: walkY, z: Math.min(this.depth - 3, cz + 10) };
      this.carveRoom(entrance.x, walkY, entrance.z, 2, 3, 2);
      this.carveRoom(exit.x, walkY, exit.z, 2, 3, 2);

      const offsets: Array<[number, number]> = [
        [-7, -3],
        [7, -3],
        [0, 7],
      ];
      for (const [ox, oz] of offsets) {
        const px = cx + ox;
        const pz = cz + oz;
        this.carveRoom(px, walkY, pz, 2, 3, 2);
        // Pedestal: short dirt pillar the totem stands on.
        this.setBlockRaw(px, floorY + 1, pz, BlockType.Stone);
        pedestals.push({ x: px, y: floorY + 2, z: pz });
      }
    } else {
      // Vault Sentinel — open square with four pillars.
      this.carveRoom(cx, walkY, cz, 9, roomH, 9);
      entrance = { x: cx, y: walkY, z: Math.max(2, cz - 9) };
      exit = { x: cx, y: walkY, z: Math.min(this.depth - 3, cz + 9) };
      this.carveRoom(entrance.x, walkY, entrance.z, 2, 3, 2);
      this.carveRoom(exit.x, walkY, exit.z, 2, 3, 2);

      for (const [ox, oz] of [
        [-4, -4],
        [4, -4],
        [-4, 4],
        [4, 4],
      ] as const) {
        for (let py = floorY + 1; py <= floorY + 4; py++) {
          this.setBlockRaw(cx + ox, py, cz + oz, BlockType.Bedrock);
        }
      }
    }

    this.setBlockRaw(exit.x, floorY + 1, exit.z, BlockType.ExitDoor);
    this.setBlockRaw(exit.x, floorY + 2, exit.z, BlockType.ExitDoor);
    this.setBlockRaw(entrance.x, floorY + 1, entrance.z, BlockType.Air);
    this.setBlockRaw(entrance.x, floorY + 2, entrance.z, BlockType.Air);

    const entranceWorld: Vector3D = {
      x: (entrance.x + 0.5) * this.blockSize,
      y: (floorY + 1) * this.blockSize + 0.9,
      z: (entrance.z + 0.5) * this.blockSize,
    };

    // Shop unused on boss floors — park coords off the arena.
    this.levels.push({
      floorY,
      ceilingY,
      entrance: entranceWorld,
      exitDoor: { x: exit.x, y: floorY + 1, z: exit.z },
      shop: { x: 1, y: floorY + 1, z: 1 },
      bossKind,
      bossPedestals: pedestals.length > 0 ? pedestals : undefined,
    });
  }

  private sampleFill(
    x: number,
    y: number,
    z: number,
    floorY: number,
    ceilingY: number,
    level: number,
    levelSeed: number,
  ): BlockType {
    // Sealed shell — unmineable.
    if (
      y === floorY ||
      y === ceilingY ||
      x === 0 ||
      z === 0 ||
      x === this.width - 1 ||
      z === this.depth - 1
    ) {
      return BlockType.Bedrock;
    }

    const depthRatio = level / Math.max(1, this.levelCount);
    const oreRoll = hashToUnit(hash3(x, y, z, levelSeed ^ 0x51feed));

    if (depthRatio > 0.55 && oreRoll < 0.028) return BlockType.Gold;
    if (depthRatio > 0.2 && oreRoll < 0.07) return BlockType.Iron;

    const n = valueNoise2(x + level * 17, z - level * 9, levelSeed, 0.12);
    if (n > 0.55 || y <= floorY + 2) return BlockType.Dirt;
    return BlockType.Stone;
  }

  private pickLandmark(
    seed: number,
    side: number,
    floorY: number,
  ): { x: number; y: number; z: number } {
    const margin = 4;
    const h = hash2(side, floorY, seed);
    // side: 0=-X, 1=+X, 2=-Z, 3=+Z
    if (side === 0) {
      const along = margin + (h % Math.max(1, this.depth - margin * 2));
      return { x: margin, y: floorY + 1, z: along };
    }
    if (side === 1) {
      const along = margin + (h % Math.max(1, this.depth - margin * 2));
      return { x: this.width - 1 - margin, y: floorY + 1, z: along };
    }
    if (side === 2) {
      const along = margin + (h % Math.max(1, this.width - margin * 2));
      return { x: along, y: floorY + 1, z: margin };
    }
    const along = margin + (h % Math.max(1, this.width - margin * 2));
    return { x: along, y: floorY + 1, z: this.depth - 1 - margin };
  }

  private carveRoom(
    cx: number,
    cy: number,
    cz: number,
    rx: number,
    ry: number,
    rz: number,
  ): void {
    for (let y = cy; y <= cy + ry; y++) {
      for (let z = cz - rz; z <= cz + rz; z++) {
        for (let x = cx - rx; x <= cx + rx; x++) {
          if (!this.inBounds(x, y, z)) continue;
          const t = this.getBlock(x, y, z);
          if (t === BlockType.Bedrock) continue;
          this.setBlockRaw(x, y, z, BlockType.Air);
        }
      }
    }
  }

  /**
   * Drunk-walk / L-path tunnel between two rooms with vertical jogs
   * so corridors aren't straight hallways.
   */
  private carveWindingTunnel(
    from: { x: number; y: number; z: number },
    to: { x: number; y: number; z: number },
    floorY: number,
    ceilingY: number,
    seed: number,
  ): void {
    let x = from.x;
    let y = from.y;
    let z = from.z;
    const walkFloor = floorY + 1;
    const walkCeil = Math.max(walkFloor + 1, ceilingY - 2);

    let guard = 0;
    while ((x !== to.x || z !== to.z) && guard < 4000) {
      guard++;
      this.carveTunnelCell(x, y, z);

      const h = hash3(x, y, z, seed ^ guard);
      const roll = hashToUnit(h);

      // Prefer closing the larger axis gap; occasionally jog vertically.
      const dx = to.x - x;
      const dz = to.z - z;
      if (roll < 0.12 && y < walkCeil) {
        y += 1;
      } else if (roll < 0.2 && y > walkFloor) {
        y -= 1;
      } else if (Math.abs(dx) > Math.abs(dz)) {
        x += Math.sign(dx) || (roll < 0.5 ? 1 : -1);
      } else if (dz !== 0) {
        z += Math.sign(dz);
      } else {
        x += Math.sign(dx) || 1;
      }

      x = Math.max(1, Math.min(this.width - 2, x));
      z = Math.max(1, Math.min(this.depth - 2, z));
      y = Math.max(walkFloor, Math.min(walkCeil, y));
    }

    // Final approach at exit height.
    this.carveTunnelCell(to.x, walkFloor, to.z);
    this.carveTunnelCell(to.x, walkFloor + 1, to.z);
  }

  private carveTunnelCell(x: number, y: number, z: number): void {
    // 2-tall, slightly wide corridor.
    for (let dy = 0; dy <= 1; dy++) {
      for (let dz = -1; dz <= 1; dz++) {
        for (let dx = -1; dx <= 0; dx++) {
          const px = x + dx;
          const py = y + dy;
          const pz = z + dz;
          if (!this.inBounds(px, py, pz)) continue;
          if (this.getBlock(px, py, pz) === BlockType.Bedrock) continue;
          this.setBlockRaw(px, py, pz, BlockType.Air);
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // InstancedMesh bookkeeping
  // ---------------------------------------------------------------------------

  /** Write logic + instance in one shot during generation. */
  private setBlockRaw(x: number, y: number, z: number, type: BlockType): void {
    const i = this.indexOf(x, y, z);
    if (i < 0) return;

    const previous = this.blocks[i] as BlockType;
    if (previous === type) return;

    const existing = this.indexToInstance[i];
    this.blocks[i] = type;

    if (!this.meshingEnabled) {
      // Generation path — instances rebuilt once neighbors are final.
      if (existing >= 0) this.removeInstance(existing);
      return;
    }

    this.refreshInstanceAt(x, y, z);
  }

  private clearInstances(): void {
    this.instanceCount = 0;
    this.indexToInstance.fill(-1);
    this.instanceToIndex.fill(-1);
    this.mesh.count = 0;
  }

  /**
   * Rebuild GPU instances for the visible Y band: only blocks that touch air
   * (or a non-opaque neighbor). Buried rock is invisible and expensive.
   */
  private rebuildVisibleInstances(): void {
    this.clearInstances();
    const y0 = this.visibleMinY;
    const y1 = this.visibleMaxY;
    const w = this.width;
    const d = this.depth;

    for (let y = y0; y <= y1; y++) {
      for (let z = 0; z < d; z++) {
        for (let x = 0; x < w; x++) {
          const i = x + z * w + y * w * d;
          const type = this.blocks[i] as BlockType;
          if (!isRenderableBlock(type)) continue;
          if (!this.isExposed(x, y, z)) continue;
          this.addInstance(i, x, y, z, type);
        }
      }
    }

    this.mesh.count = this.instanceCount;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) {
      this.mesh.instanceColor.needsUpdate = true;
    }
    this.atlasIndexAttr.needsUpdate = true;
  }

  /** Sync one voxel's instance with current type / exposure / visibility. */
  private refreshInstanceAt(x: number, y: number, z: number): void {
    const i = this.indexOf(x, y, z);
    if (i < 0) return;

    const type = this.blocks[i] as BlockType;
    const existing = this.indexToInstance[i];
    const want = this.shouldHaveInstance(x, y, z, type);

    if (want) {
      if (existing >= 0) {
        this.writeInstanceMatrix(existing, x, y, z);
        this.writeInstanceColor(existing, type);
      } else {
        this.addInstance(i, x, y, z, type);
        this.mesh.count = this.instanceCount;
      }
    } else if (existing >= 0) {
      this.removeInstance(existing);
    }
  }

  private shouldHaveInstance(x: number, y: number, z: number, type: BlockType): boolean {
    if (!isRenderableBlock(type)) return false;
    if (y < this.visibleMinY || y > this.visibleMaxY) return false;
    return this.isExposed(x, y, z);
  }

  /** True when any face touches a non-opaque cell (or the world edge). */
  private isExposed(x: number, y: number, z: number): boolean {
    return (
      !this.isOpaqueAt(x + 1, y, z) ||
      !this.isOpaqueAt(x - 1, y, z) ||
      !this.isOpaqueAt(x, y + 1, z) ||
      !this.isOpaqueAt(x, y - 1, z) ||
      !this.isOpaqueAt(x, y, z + 1) ||
      !this.isOpaqueAt(x, y, z - 1)
    );
  }

  private isOpaqueAt(x: number, y: number, z: number): boolean {
    if (!this.inBounds(x, y, z)) return false;
    return isOpaqueBlock(this.blocks[this.indexOf(x, y, z)] as BlockType);
  }

  private addInstance(voxelIndex: number, x: number, y: number, z: number, type: BlockType): void {
    const id = this.instanceCount++;
    this.indexToInstance[voxelIndex] = id;
    this.instanceToIndex[id] = voxelIndex;
    this.writeInstanceMatrix(id, x, y, z);
    this.writeInstanceColor(id, type);
  }

  private removeInstance(id: number): void {
    const last = this.instanceCount - 1;
    const voxelIndex = this.instanceToIndex[id];
    this.indexToInstance[voxelIndex] = -1;

    if (id !== last) {
      const movedVoxel = this.instanceToIndex[last];
      this.instanceToIndex[id] = movedVoxel;
      this.indexToInstance[movedVoxel] = id;

      this.mesh.getMatrixAt(last, this.matrix);
      this.mesh.setMatrixAt(id, this.matrix);
      this.atlasIndexAttr.setX(id, this.atlasIndexAttr.getX(last));
      this.atlasIndexAttr.needsUpdate = true;
      if (this.mesh.instanceColor) {
        this.mesh.getColorAt(last, this.scratchColor);
        this.mesh.setColorAt(id, this.scratchColor);
      }
    }

    this.mesh.setMatrixAt(last, HIDDEN_MATRIX);
    this.instanceToIndex[last] = -1;
    this.instanceCount = last;
    this.mesh.count = this.instanceCount;
  }

  private writeInstanceMatrix(instanceId: number, x: number, y: number, z: number): void {
    const s = this.blockSize;
    this.matrix.makeScale(s, s, s);
    this.matrix.setPosition((x + 0.5) * s, (y + 0.5) * s, (z + 0.5) * s);
    this.mesh.setMatrixAt(instanceId, this.matrix);
  }

  private writeInstanceColor(instanceId: number, type: BlockType): void {
    this.atlasIndexAttr.setX(instanceId, BLOCK_ATLAS_INDEX[type] ?? 1);
    this.atlasIndexAttr.needsUpdate = true;
    if (!this.mesh.instanceColor) return;
    this.mesh.setColorAt(instanceId, BLOCK_TINTS[type] ?? BLOCK_TINTS[BlockType.Stone]);
  }
}

/** Blocks that should never get a world-mesh instance. */
function isRenderableBlock(type: BlockType): boolean {
  return type !== BlockType.Air && type !== BlockType.ExitDoor;
}

/** Opaque for exposure tests — air / doors / shops carve visibility. */
function isOpaqueBlock(type: BlockType): boolean {
  return (
    type !== BlockType.Air &&
    type !== BlockType.ExitDoor &&
    type !== BlockType.SurfacePortal &&
    type !== BlockType.Shop
  );
}

function createAtlasLambertMaterial(atlas: CanvasTexture): MeshLambertMaterial {
  const material = new MeshLambertMaterial({
    map: atlas,
    vertexColors: false,
  });

  material.onBeforeCompile = (shader) => {
    shader.uniforms.uAtlasCols = { value: BLOCK_ATLAS_COLS };
    shader.uniforms.uAtlasRows = { value: BLOCK_ATLAS_ROWS };

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        /* glsl */ `
        #include <common>
        attribute float instanceAtlasIndex;
        varying vec2 vAtlasOffset;
        varying vec2 vAtlasScale;
        uniform float uAtlasCols;
        uniform float uAtlasRows;
        `,
      )
      .replace(
        '#include <uv_vertex>',
        /* glsl */ `
        #include <uv_vertex>
        float atlasCol = mod(instanceAtlasIndex, uAtlasCols);
        float atlasRow = floor(instanceAtlasIndex / uAtlasCols);
        vAtlasScale = vec2(1.0 / uAtlasCols, 1.0 / uAtlasRows);
        vAtlasOffset = vec2(atlasCol * vAtlasScale.x, 1.0 - (atlasRow + 1.0) * vAtlasScale.y);
        `,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        /* glsl */ `
        #include <common>
        varying vec2 vAtlasOffset;
        varying vec2 vAtlasScale;
        `,
      )
      .replace(
        '#include <map_fragment>',
        /* glsl */ `
        #ifdef USE_MAP
          vec4 sampledDiffuseColor = texture2D(map, vAtlasOffset + vMapUv * vAtlasScale);
          diffuseColor *= sampledDiffuseColor;
        #endif
        `,
      );
  };

  // Force shader recompile key so Three doesn't reuse a stock Lambert cache entry.
  material.customProgramCacheKey = () => 'minelunky-block-atlas-v1';
  return material;
}

function createUnitBoxGeometry(): BufferGeometry {
  const geometry = new BufferGeometry();
  const s = 0.5;
  const positions = new Float32Array([
    s, -s, -s, s, s, -s, s, s, s, s, -s, -s, s, s, s, s, -s, s,
    -s, -s, s, -s, s, s, -s, s, -s, -s, -s, s, -s, s, -s, -s, -s, -s,
    -s, s, -s, -s, s, s, s, s, s, -s, s, -s, s, s, s, s, s, -s,
    -s, -s, s, -s, -s, -s, s, -s, -s, -s, -s, s, s, -s, -s, s, -s, s,
    -s, -s, s, s, -s, s, s, s, s, -s, -s, s, s, s, s, -s, s, s,
    s, -s, -s, -s, -s, -s, -s, s, -s, s, -s, -s, -s, s, -s, s, s, -s,
  ]);
  const normals = new Float32Array([
    1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0,
    -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0,
    0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0,
    0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0,
    0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1,
    0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1,
  ]);
  // Per-face 0..1 UVs so the atlas tile maps cleanly onto every side.
  const uvs = new Float32Array([
    0, 0, 0, 1, 1, 1, 0, 0, 1, 1, 1, 0,
    0, 0, 0, 1, 1, 1, 0, 0, 1, 1, 1, 0,
    0, 0, 0, 1, 1, 1, 0, 0, 1, 1, 1, 0,
    0, 0, 0, 1, 1, 1, 0, 0, 1, 1, 1, 0,
    0, 0, 0, 1, 1, 1, 0, 0, 1, 1, 1, 0,
    0, 0, 0, 1, 1, 1, 0, 0, 1, 1, 1, 0,
  ]);
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  return geometry;
}
